import { BreakPointDTO, TripDTO } from '@/types/controlsheet'
import { getTimeDifferenceInMinutes } from './timeDiff'
import { standardizeTime } from './standardizeTime'

type Phase = 'beforeShift' | 'beforeBreak' | 'duringBreak' | 'afterBreak' | 'afterShift'

export const getPhase = (time: string, trip: TripDTO): Phase => {
    const shiftStartTime = standardizeTime(trip.shiftStartTime)
    const breakStartTime = standardizeTime(trip.breakStartTime)
    const breakEndTime = standardizeTime(trip.breakEndTime)
    const shiftEndTime = standardizeTime(trip.shiftEndTime)

    if (getTimeDifferenceInMinutes(shiftStartTime, time)! < 0) {
        return 'beforeShift'
    }

    if (getTimeDifferenceInMinutes(breakStartTime, time)! < 0) {
        return 'beforeBreak'
    }

    if (getTimeDifferenceInMinutes(breakEndTime, time)! < 0) {
        return 'duringBreak'
    }

    if (getTimeDifferenceInMinutes(shiftEndTime, time)! < 0) {
        return 'afterBreak'
    }

    return 'afterShift'
}

const orderBreakpoints = (breakpoints: (BreakPointDTO | null)[]): BreakPointDTO[] => {
    // Double triple check all breakpoints are ordered by time!
    return breakpoints
        .filter((bp) => bp !== null)
        .sort((a, b) => {
            return getTimeDifferenceInMinutes(b.time, a.time) || 0
        })
}

export const tripContainsMealRelief = (trip: TripDTO): boolean => {
    const breakpoints = orderBreakpoints(trip.breakPoints)
    const firstBreakpoint: BreakPointDTO = breakpoints[0]
    const lastBreakpoint: BreakPointDTO = breakpoints[breakpoints.length - 1]

    if (!firstBreakpoint || !firstBreakpoint.time || !lastBreakpoint || !lastBreakpoint.time) {
        return false
    }

    const firstBreakpointPhase = getPhase(firstBreakpoint.time, trip)

    if (firstBreakpointPhase === 'beforeBreak') {
        for (let i = 1; i < breakpoints.length; i++) {
            const bp = breakpoints[i]
            if (bp && bp.time) {
                const bpPhase = getPhase(bp.time, trip)
                if (bpPhase === 'duringBreak') {
                    return true
                }
            }
        }
    }

    return false
}

export const spellTimeForTrip = (trip: TripDTO): number => {
    // Double triple check all breakpoints are ordered by time!
    const breakpoints = orderBreakpoints(trip.breakPoints)

    const firstBreakpoint: BreakPointDTO = breakpoints[0]
    const lastBreakpoint: BreakPointDTO = breakpoints[breakpoints.length - 1]

    if (!firstBreakpoint || !firstBreakpoint.time || !lastBreakpoint || !lastBreakpoint.time) {
        return 0
    }

    const firstBreakpointPhase = getPhase(firstBreakpoint.time, trip)

    if (firstBreakpointPhase === 'beforeShift') {
        return getTimeDifferenceInMinutes(trip.shiftStartTime, lastBreakpoint.time) || 0
    }

    if (firstBreakpointPhase === 'beforeBreak') {
        // Check to see if the break starts in this trip
        for (let i = 1; i < breakpoints.length; i++) {
            const bp = breakpoints[i]
            if (bp && bp.time) {
                const bpPhase = getPhase(bp.time, trip)
                if (bpPhase === 'duringBreak') {
                    return getTimeDifferenceInMinutes(trip.shiftStartTime, trip.breakStartTime) || 0
                }
            }
        }

        // We got here so the break does not start in this trip
        const timeDiff = getTimeDifferenceInMinutes(trip.shiftStartTime, lastBreakpoint.time)
        return timeDiff ? timeDiff : 0
    }

    if (firstBreakpointPhase === 'duringBreak') {
        const firstAfterBreak = breakpoints.find((bp) => {
            return bp && bp.time && getPhase(bp.time, trip) === 'afterBreak'
        })

        if (!firstAfterBreak || !firstAfterBreak.time) {
            return 0
        }

        const timeDiff = getTimeDifferenceInMinutes(trip.breakStartTime, firstAfterBreak.time)
        return timeDiff ? timeDiff : 0
    }

    if (firstBreakpointPhase === 'afterBreak') {
        // Check to see if the shift ends in this trip
        for (let i = 1; i < breakpoints.length; i++) {
            const bp = breakpoints[i]
            if (bp && bp.time) {
                const bpPhase = getPhase(bp.time, trip)
                if (bpPhase === 'afterShift') {
                    const timeDiff = getTimeDifferenceInMinutes(trip.breakEndTime, trip.shiftEndTime)
                    return timeDiff ? timeDiff : 0
                }
            }
        }

        // If we got here, the shift does not end in this trip
        const timeDiff = getTimeDifferenceInMinutes(trip.breakEndTime, lastBreakpoint.time)
        return timeDiff ? timeDiff : 0
    }

    console.error("Don't expect to ever get here! firstBreakpointPhase:", firstBreakpointPhase)
    return 0
}
